'use client'

import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, P, Quiz, Step } from '../_components/lesson'
import { FunctionMachine } from '../_components/widgets/CodeVisuals'
import * as EX from './exercises/code-basics'

export default function Functions() {
  return (
    <Lesson>
      <Step>
        <H>Little machines</H>
        <P>
          A <strong>function</strong> is a machine with a rule inside. You drop an <Hl c="blue">input</Hl> in, it applies the
          rule, and an <Hl c="amber">output</Hl> comes out. Same input, same output, every time.
        </P>
        <Figure n="1.2" caption="Pick a machine and feed it different inputs. The rule never changes — only what goes in.">
          <FunctionMachine />
        </Figure>
        <P>
          Mathematicians write this as <M>{'f(\\blue{x}) = 2\\blue{x} + 1'}</M>: “the function called <em>f</em>, given{' '}
          <em>x</em>, gives back 2 times x plus 1”. It’s exactly the same idea in code — and it’s the single most important idea
          in this course, because <strong>a machine-learning model is just a function</strong> whose rule was learned instead of
          written.
        </P>
      </Step>

      <Step>
        <H>Writing your own</H>
        <P>
          In TypeScript you build a machine with <C>function</C>, give it a name, list its inputs (called{' '}
          <strong>parameters</strong>) in brackets, and use <C>return</C> to send the output back.
        </P>
        <Exercise id="first-function" title="Defining and calling" {...EX.firstFunction} />
        <Callout kind="notation">
          <C>(x: number): number</C> reads as “takes a number called x, returns a number”. The shorter arrow form{' '}
          <C>{'(x: number) => x * 3'}</C> means exactly the same thing — you’ll see both.
        </Callout>
      </Step>

      <Step gate="square">
        <H>Your turn</H>
        <Exercise
          id="square"
          title="Two machines"
          {...EX.square}
          hints={[
            <>
              <C>return x * x</C> squares a number.
            </>,
            <>
              Write the formula left to right: <C>c * 9 / 5 + 32</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="abs-clamp">
        <H>Machines that decide</H>
        <P>
          With <C>if</C>, a function can do different things depending on its input. Read it like English: <em>if</em> this is
          true, do that; <em>else</em> do something else.
        </P>
        <Exercise id="if-else" title="if / else if / else" {...EX.ifElse} />
        <P>
          Now build two tiny decision-making machines. You’ll use both of them later: <C>abs</C> measures “how far off” a
          prediction is, and <C>clamp</C> keeps numbers from running wild during training.
        </P>
        <Exercise
          id="abs-clamp"
          title="abs and clamp"
          {...EX.absClamp}
          hints={[
            <>
              For <C>abs</C>: if x is below 0, return <C>-x</C> (the minus flips the sign).
            </>,
            <>
              For <C>clamp</C>: two ifs — one for “too small”, one for “too big” — and otherwise return x unchanged.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-compose">
        <H>Functions feeding functions</H>
        <P>
          The output of one machine can be the input of another. This is called <strong>composition</strong>, written{' '}
          <M>{'f(g(\\blue{x}))'}</M>: do g first, then feed the result to f. And because functions are values, you can hand a
          whole function to another one — that’s how <C>plot.fn</C> knows what to draw.
        </P>
        <Exercise id="fn-values" title="Passing functions around" {...EX.fnValues} />
        <Quiz
          id="q-compose"
          question={
            <>
              With <M>{'f(x) = x^2 - 2'}</M> and <M>{'g(x) = 2x + 1'}</M>, what is <M>{'f(g(1))'}</M>?
            </>
          }
          options={[
            { label: '7', correct: true },
            { label: '3', why: 'That’s g(1) = 2·1 + 1. Now feed that 3 into f.' },
            { label: '−1', why: 'That’s f(1). In f(g(1)), g goes first.' },
            { label: '−3', why: 'That would be g(f(1)) — the other order.' },
          ]}
          explain="Inside out: g(1) = 3, then f(3) = 9 − 2 = 7. Deep neural networks are exactly this — dozens of functions, each feeding the next."
        />
      </Step>

      <Step>
        <H>Why this matters</H>
        <P>
          Hold on to this picture. A neural network is a function. A layer of a neural network is a function. Training is a
          function that improves another function. The giant language models are one enormous composed function from text to
          “what comes next” — and you’ll build a small one yourself.
        </P>
      </Step>
    </Lesson>
  )
}
